import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { FiArrowLeft } from 'react-icons/fi';
import { useQuestionBook } from '@/hooks/useQuestionBook';
import { Loader } from '@/components/custom/loader';
import tiaExercicios from '@/assets/images/tia/TIA-EXERCICIOS.png';

// Modos de treino: rótulos amigáveis mapeados nos interactionTypes que o
// createQuestionBook já entende.
const MODES = [
  { key: 'new', emoji: '🆕', label: 'Questões novas', desc: 'As que você ainda não respondeu', types: ['neverAnswered'] },
  { key: 'review', emoji: '🔁', label: 'Revisar meus erros', desc: 'As que você errou ou nunca acertou', types: ['gotAndWrong', 'neverGot'] },
  { key: 'mix', emoji: '🎲', label: 'Misturar', desc: 'Um pouco de tudo', types: ['neverAnswered', 'neverGot', 'gotAndWrong'] }
];

// Cabeçalho conversacional da T.I.A (avatar + balão com a pergunta).
function TiaAsk({ children }) {
  return (
    <div className="mb-6 flex items-end gap-3">
      <img
        src={tiaExercicios}
        alt=""
        aria-hidden="true"
        className="pointer-events-none h-24 w-auto shrink-0 self-end object-contain object-bottom"
      />
      <div className="relative z-10 mb-2 flex-1 rounded-2xl rounded-bl-none bg-white p-4 shadow-card">
        <span
          className="block text-xs font-extrabold"
          style={{ color: 'var(--color-secondary-dark)' }}
        >
          T.I.A
        </span>
        <p className="m-0 text-sm font-bold leading-relaxed text-neutral-dark">{children}</p>
      </div>
    </div>
  );
}

// Criação conversacional de caderno de exercícios (2 passos, guiado pela T.I.A).
// Substitui o form técnico no fluxo playful; reaproveita createQuestionBook.
export function ExercisesCreateWizard({ setShowModal }) {
  const client = useClient();
  const { createQuestionBook } = useQuestionBook();
  const [materias, setMaterias] = useState(null); // null = carregando
  const [materia, setMateria] = useState(null); // { id, name } — null = passo 1
  const [creatingKey, setCreatingKey] = useState(null);

  useEffect(() => {
    let active = true;
    client
      .fetch('taxonomies/used_by_questions')
      .then(({ data }) => {
        if (active) setMaterias((data ?? []).map(tax => ({ id: tax.id, name: tax.name })));
      })
      .catch(() => {
        if (active) setMaterias([]);
      });
    return () => {
      active = false;
    };
  }, [client]);

  const handleCreate = async mode => {
    if (creatingKey) return;
    setCreatingKey(mode.key);
    // O backend casa taxonomy_id EXATO (sem expandir descendentes) e as questões
    // são tagueadas nos descritores-folha, não na matéria. Então expandimos a
    // matéria escolhida nos seus descritores (mesma fonte do Banco de Questões).
    // Fallback pro id da matéria se a busca falhar (livro vazio → toast do provider).
    let taxonomyIds = [materia.id];
    try {
      const { data } = await client.fetch(`taxonomies/used_by_questions?parent_id=${materia.id}`);
      const ids = (data ?? []).map(tax => tax.id);
      if (ids.length) taxonomyIds = ids;
    } catch {
      /* mantém o fallback [materia.id] */
    }
    // O provider trata sucesso/erro (inclusive "sem questões pro filtro") via toast.
    await createQuestionBook({
      title: `Treino de ${materia.name}`,
      taxonomyIds,
      interactionTypes: mode.types,
      published: true
    });
    setCreatingKey(null);
    setShowModal(false);
  };

  // Passo 1 — escolher a matéria (seleção única, avança ao tocar).
  if (!materia) {
    return (
      <div>
        <TiaAsk>Que matéria vamos treinar? 📚</TiaAsk>

        {materias === null ? (
          <div className="grid place-items-center py-10">
            <Loader />
          </div>
        ) : materias.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral">Nenhuma matéria disponível no momento.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {materias.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMateria(m)}
                className="rounded-full border-2 border-neutral-lighter bg-white px-4 py-2.5 text-sm font-bold text-neutral-dark transition-transform hover:border-[var(--color-mission)] active:scale-95"
              >
                {m.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Passo 2 — escolher o modo (tap cria).
  return (
    <div>
      <button
        type="button"
        onClick={() => setMateria(null)}
        className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-neutral transition-opacity hover:opacity-70"
      >
        <FiArrowLeft className="h-4 w-4" /> Trocar matéria
      </button>

      <TiaAsk>
        Bora treinar <b>{materia.name}</b>! Quer questões novas ou revisar? ⚡
      </TiaAsk>

      <div className="flex flex-col gap-3">
        {MODES.map(mode => (
          <button
            key={mode.key}
            type="button"
            disabled={!!creatingKey}
            onClick={() => handleCreate(mode)}
            className="flex items-center gap-3 rounded-2xl border-2 border-neutral-lighter bg-white p-4 text-left transition-all hover:border-[var(--color-mission)] active:scale-[0.98] disabled:opacity-60"
          >
            <span className="text-2xl">{mode.emoji}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-extrabold text-neutral-dark">{mode.label}</span>
              <span className="block text-xs text-neutral">{mode.desc}</span>
            </span>
            {creatingKey === mode.key && (
              <span
                aria-hidden="true"
                className="h-5 w-5 shrink-0 animate-spinner rounded-full [-webkit-mask:radial-gradient(farthest-side,transparent_calc(100%-3px),#000_0)]"
                style={{ background: 'radial-gradient(farthest-side,#8894AC 94%,#0000) top/3px 3px no-repeat, conic-gradient(#0000 30%,#8894AC)' }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
